import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { invoicesAPI, clientsAPI, projectsAPI } from '../lib/api';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel, getInvoiceTypeLabel, getRecurrenceLabel, isOverdue } from '../lib/utils';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  CheckCircle,
  CalendarIcon,
  Receipt,
  Send,
  AlertTriangle,
  Pencil,
} from 'lucide-react';
import { QuickClientDialog } from '../components/QuickClientDialog';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const invoiceSchema = z.object({
  titolo: z.string().min(2, 'Titolo richiesto'),
  client_id: z.string().min(1, 'Cliente richiesto'),
  project_id: z.string().optional(),
  importo: z.coerce.number().min(0, 'Importo non valido'),
  tipo: z.enum(['acconto', 'saldo', 'ricorrente']),
  data_scadenza: z.date({ required_error: 'Data scadenza richiesta' }),
  ricorrenza: z.enum(['mensile', 'trimestrale', 'semestrale', 'annuale']).optional().nullable(),
  note: z.string().optional(),
});

export const Fatturazione = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      tipo: 'saldo',
      importo: 0,
    },
  });

  const selectedType = watch('tipo');
  const selectedClient = watch('client_id');
  const selectedProjectId = watch('project_id');
  const [accontoPct, setAccontoPct] = useState(30);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      projectsAPI.getAll(selectedClient).then(res => setProjects(res.data));
    }
  }, [selectedClient]);

  // Auto-calcola importo per acconto/saldo quando è selezionato un progetto
  useEffect(() => {
    const pid = selectedProjectId === 'none' ? null : selectedProjectId;
    if (!pid || editingInvoice) return;
    const proj = projects.find(p => p.id === pid);
    if (!proj?.budget) return;
    const budget = proj.budget;
    const accontiEsistenti = invoices
      .filter(i => i.project_id === pid && i.tipo === 'acconto')
      .reduce((s, i) => s + (i.importo || 0), 0);
    if (selectedType === 'acconto') {
      setValue('importo', Math.round(budget * accontoPct / 100 * 100) / 100);
    } else if (selectedType === 'saldo') {
      setValue('importo', Math.max(0, Math.round((budget - accontiEsistenti) * 100) / 100));
    }
  }, [selectedType, selectedProjectId, accontoPct, projects]);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        invoicesAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setClients(clientsRes.data);

      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        data_scadenza: data.data_scadenza?.toISOString(),
        ricorrenza: data.tipo === 'ricorrente' ? data.ricorrenza : null,
      };
      if (editingInvoice) {
        await invoicesAPI.update(editingInvoice.id, payload);
        toast.success('Fattura aggiornata');
      } else {
        await invoicesAPI.create(payload);
        toast.success('Fattura creata');
      }
      fetchData();
      setDialogOpen(false);
      setEditingInvoice(null);
      reset({ tipo: 'saldo', importo: 0 }); setAccontoPct(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    reset({
      titolo: invoice.titolo,
      client_id: invoice.client_id,
      project_id: invoice.project_id || undefined,
      importo: invoice.importo,
      tipo: invoice.tipo,
      data_scadenza: new Date(invoice.data_scadenza),
      ricorrenza: invoice.ricorrenza || undefined,
      note: invoice.note || '',
    });
    if (invoice.client_id) {
      projectsAPI.getAll(invoice.client_id).then(res => setProjects(res.data));
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fattura?')) return;
    try {
      await invoicesAPI.delete(id);
      toast.success('Fattura eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await invoicesAPI.updateStatus(id, status);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.nome || 'N/A';
  };

  const currentYear = new Date().getFullYear();

  const inYear = (inv) => {
    if (yearFilter === 'all') return true;
    const yr = parseInt(yearFilter);
    const ref = inv.data_pagamento || inv.data_scadenza || inv.created_at;
    return ref ? new Date(ref).getFullYear() === yr : false;
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.titolo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.numero.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getClientName(invoice.client_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.stato === statusFilter;
    const matchesYear = inYear(invoice);
    return matchesSearch && matchesStatus && matchesYear;
  });

  // Stats (calcolate sull'anno selezionato)
  const yearInvoices = invoices.filter(inYear);
  const totalAmount = yearInvoices.reduce((sum, inv) => sum + (inv.importo || 0), 0);
  const paidAmount = yearInvoices.filter(inv => inv.stato === 'pagata').reduce((sum, inv) => sum + (inv.importo || 0), 0);
  const pendingAmount = yearInvoices.filter(inv => inv.stato !== 'pagata').reduce((sum, inv) => sum + (inv.importo || 0), 0);
  const overdueCount = yearInvoices.filter(inv => inv.stato === 'scaduta').length;

  return (
    <Layout title="Fatturazione">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="invoice-stats">
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Totale Fatturato</p>
            <p className="text-xl font-bold currency mt-1">{formatCurrency(totalAmount)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Incassato</p>
            <p className="text-xl font-bold text-emerald-600 currency mt-1">{formatCurrency(paidAmount)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Da Incassare</p>
            <p className="text-xl font-bold text-amber-600 currency mt-1">{formatCurrency(pendingAmount)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Scadute</p>
            <p className={`text-xl font-bold mt-1 ${overdueCount > 0 ? 'text-red-600' : 'text-slate-600'}`}>
              {overdueCount}
            </p>
          </Card>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca fatture..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-invoices-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="filter-invoice-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="da_emettere">Da emettere</SelectItem>
                <SelectItem value="emessa">Emessa</SelectItem>
                <SelectItem value="pagata">Pagata</SelectItem>
                <SelectItem value="scaduta">Scaduta</SelectItem>
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(currentYear)}>{currentYear}</SelectItem>
                <SelectItem value={String(currentYear - 1)}>{currentYear - 1}</SelectItem>
                <SelectItem value={String(currentYear - 2)}>{currentYear - 2}</SelectItem>
                <SelectItem value="all">Tutti gli anni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) { setEditingInvoice(null); reset({ tipo: 'saldo', importo: 0 }); setAccontoPct(30); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-invoice-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuova Fattura
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingInvoice ? 'Modifica Fattura' : 'Nuova Fattura'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input {...register('titolo')} data-testid="invoice-titolo-input" />
                  {errors.titolo && <p className="text-sm text-red-500">{errors.titolo.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <div className="flex gap-2">
                      <Controller
                        name="client_id"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger data-testid="invoice-client-select" className="flex-1">
                              <SelectValue placeholder="Seleziona cliente" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <QuickClientDialog
                        onCreated={(c) => {
                          setClients((prev) => [...prev, c]);
                          reset({ ...watch(), client_id: c.id });
                        }}
                      />
                    </div>
                    {errors.client_id && <p className="text-sm text-red-500">{errors.client_id.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Progetto</Label>
                    <Controller
                      name="project_id"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || 'none'}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona progetto" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuno</SelectItem>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Tipo + importo con logica acconto/saldo */}
                {(() => {
                  const pid = selectedProjectId === 'none' ? null : selectedProjectId;
                  const proj = pid ? projects.find(p => p.id === pid) : null;
                  const budget = proj?.budget || 0;
                  const accontiEsistenti = pid
                    ? invoices.filter(i => i.project_id === pid && i.tipo === 'acconto').reduce((s, i) => s + (i.importo || 0), 0)
                    : 0;
                  const saldoCalcolato = Math.max(0, budget - accontiEsistenti);

                  return (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipo *</Label>
                          <Controller
                            name="tipo"
                            control={control}
                            render={({ field }) => (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger data-testid="invoice-tipo-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="acconto">Acconto</SelectItem>
                                  <SelectItem value="saldo">Saldo</SelectItem>
                                  <SelectItem value="ricorrente">Ricorrente</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        {selectedType === 'acconto' && budget > 0 ? (
                          <div className="space-y-2">
                            <Label>Percentuale acconto</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number" min={1} max={100} step={1}
                                value={accontoPct}
                                onChange={e => setAccontoPct(Math.max(1, Math.min(100, Number(e.target.value))))}
                                className="w-20 text-right"
                              />
                              <span className="text-sm text-slate-500">%</span>
                              <span className="text-sm text-slate-400">di {formatCurrency(budget)}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label>Importo (€) *</Label>
                            <Input type="number" step="0.01" {...register('importo')} data-testid="invoice-importo-input" />
                            {errors.importo && <p className="text-sm text-red-500">{errors.importo.message}</p>}
                          </div>
                        )}
                      </div>

                      {/* Riepilogo saldo */}
                      {selectedType === 'saldo' && budget > 0 && (
                        <div className="rounded-lg bg-slate-50 border px-4 py-2.5 space-y-1 text-sm">
                          <div className="flex justify-between text-slate-500">
                            <span>Budget progetto</span>
                            <span>{formatCurrency(budget)}</span>
                          </div>
                          {accontiEsistenti > 0 && (
                            <div className="flex justify-between text-slate-500">
                              <span>Acconti già fatturati</span>
                              <span className="text-amber-600">− {formatCurrency(accontiEsistenti)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold text-slate-800 border-t pt-1 mt-1">
                            <span>Saldo da fatturare</span>
                            <span className="text-emerald-700">{formatCurrency(saldoCalcolato)}</span>
                          </div>
                        </div>
                      )}

                      {/* Importo manuale quando è acconto con budget */}
                      {selectedType === 'acconto' && budget > 0 && (
                        <div className="space-y-1">
                          <Label>Importo (€) *</Label>
                          <Input type="number" step="0.01" {...register('importo')} data-testid="invoice-importo-input" />
                          {errors.importo && <p className="text-sm text-red-500">{errors.importo.message}</p>}
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Scadenza *</Label>
                    <Controller
                      name="data_scadenza"
                      control={control}
                      render={({ field }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start font-normal"
                              data-testid="invoice-data-btn"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : 'Seleziona data'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              locale={it}
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                    {errors.data_scadenza && <p className="text-sm text-red-500">{errors.data_scadenza.message}</p>}
                  </div>
                  {selectedType === 'ricorrente' && (
                    <div className="space-y-2">
                      <Label>Ricorrenza</Label>
                      <Controller
                        name="ricorrenza"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value || ''}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mensile">Mensile</SelectItem>
                              <SelectItem value="trimestrale">Trimestrale</SelectItem>
                              <SelectItem value="semestrale">Semestrale</SelectItem>
                              <SelectItem value="annuale">Annuale</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea {...register('note')} rows={2} />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-invoice-btn">
                    {editingInvoice ? 'Salva' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <Card className="table-wrapper" data-testid="invoices-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead>Numero</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead className="hidden md:table-cell">Cliente</TableHead>
                <TableHead className="hidden sm:table-cell">Importo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="hidden lg:table-cell">Scadenza</TableHead>
                <TableHead className="w-20">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                    {searchQuery || statusFilter !== 'all' ? 'Nessun risultato' : 'Nessuna fattura'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="table-row-hover">
                    <TableCell className="font-mono text-sm text-slate-500">{invoice.numero}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {invoice.stato !== 'pagata' && isOverdue(invoice.data_scadenza) && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                        {invoice.titolo}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500">
                      {getClientName(invoice.client_id)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell font-medium currency">
                      {formatCurrency(invoice.importo)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getInvoiceTypeLabel(invoice.tipo)}</Badge>
                      {invoice.ricorrenza && (
                        <span className="text-xs text-slate-400 ml-1">
                          ({getRecurrenceLabel(invoice.ricorrenza)})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(invoice.stato)}>
                        {getStatusLabel(invoice.stato)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-500">
                      {formatDate(invoice.data_scadenza)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`invoice-menu-${invoice.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(invoice)}>
                            <Pencil className="h-4 w-4 mr-2" /> Modifica
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {invoice.stato === 'da_emettere' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'emessa')}>
                              <Send className="h-4 w-4 mr-2" /> Segna come Emessa
                            </DropdownMenuItem>
                          )}
                          {(invoice.stato === 'emessa' || invoice.stato === 'scaduta') && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'pagata')}>
                              <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" /> Segna come Pagata
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(invoice.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
};
